import { CompetitorPriceSource, type ItemIntel } from '@morales/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../api';
import { Empty, ErrorBox, PageHead, QueryView } from '../components/ui';
import { formatDate, formatMoney, formatQty } from '../format';
import { useLocale } from '../i18n';
import { qk } from '../queryClient';
import { parseNumber } from './StockPage';

/** A tiny bar chart: weekly values, the newest on the right, drawn in CSS so it prints and works offline. */
function Bars({ points, label, money, intlLocale }: { points: { week: string; value: number | null }[]; label: string; money: boolean; intlLocale: string }) {
  const values = points.map((p) => p.value).filter((v): v is number => v !== null);
  if (values.length === 0) return <p className="muted">{label}: —</p>;
  const max = Math.max(...values);
  const min = Math.min(...values);
  return (
    <div className="bars" role="img" aria-label={label}>
      {points.map((p) => (
        <span key={p.week} className="bar" title={`${formatDate(p.week, intlLocale, 'date')}: ${p.value === null ? '—' : money ? formatMoney(p.value, intlLocale) : p.value}`}>
          <span className="bar-fill" style={{ height: p.value === null ? 0 : `${Math.max(8, Math.round(((p.value - min * 0.8) / (max - min * 0.8 || 1)) * 100))}%` }} />
        </span>
      ))}
      <span className="bar-range">
        {money ? formatMoney(min, intlLocale) : min} – {money ? formatMoney(max, intlLocale) : max}
      </span>
    </div>
  );
}

/** "What are tomatoes doing this week": cost, what we sold it for, tiers, who paid what, competitors, margin. */
export function IntelPage() {
  const { t, intlLocale } = useLocale();
  const { id = '' } = useParams();
  const query = useQuery({ queryKey: qk.intel(id), queryFn: ({ signal }) => api.intel(id, signal) });
  return (
    <QueryView query={query}>
      {(x: ItemIntel) => (
        <div className="stack">
          <PageHead title={`${t('intelTitle')}: ${x.itemName}`} back={`/items/${x.itemId}`} />
          <section className="card stack-sm">
            <h2>{t('thisWeek')}</h2>
            <dl className="facts plain">
              <div className="fact">
                <dt>{t('onHand')}</dt>
                <dd>
                  {formatQty(x.onHandPacks)} {x.packUnit}
                </dd>
              </div>
              <div className="fact">
                <dt>{t('sold7')}</dt>
                <dd>
                  {formatQty(x.soldLast7)} {x.packUnit}
                </dd>
              </div>
              <div className="fact">
                <dt>{t('sold28')}</dt>
                <dd>
                  {formatQty(x.soldLast28)} {x.packUnit}
                </dd>
              </div>
              {x.marginNow !== null || x.marginBefore !== null ? (
                <div className="fact">
                  <dt>{t('marginNowBefore')}</dt>
                  <dd>
                    {x.marginNow === null ? '—' : `${x.marginNow}%`} · {x.marginBefore === null ? '—' : `${x.marginBefore}%`}
                  </dd>
                </div>
              ) : null}
            </dl>
          </section>

          {x.costs.length > 0 ? (
            <section className="card stack-sm">
              <h2>{t('cost')}</h2>
              <ul className="list">
                {x.costs.map((c) => (
                  <li key={c.vendorId} className="row">
                    <span className="row-main">
                      <span className="row-title">{c.vendorName}</span>
                      <span className="row-sub">
                        {formatDate(c.effectiveDate, intlLocale, 'date')} · {c.source}
                        {c.prior !== null ? ` · ${t('was')} ${formatMoney(c.prior, intlLocale)}` : ''}
                      </span>
                    </span>
                    <span className={c.prior !== null && c.cost > c.prior ? 'row-end-value owed' : 'row-end-value'}>{formatMoney(c.cost, intlLocale)}</span>
                  </li>
                ))}
              </ul>
              <Bars points={x.costWeeks} label={t('costWeeks')} money intlLocale={intlLocale} />
            </section>
          ) : null}

          <section className="card stack-sm">
            <h2>{t('soldFor')}</h2>
            <Bars points={x.soldWeeks} label={t('soldWeeks')} money intlLocale={intlLocale} />
            <dl className="facts plain">
              {x.tiers.map((tier) => (
                <div key={tier.code} className="fact">
                  <dt>{tier.name}</dt>
                  <dd className="money">{tier.price === null ? t('noPrice') : formatMoney(tier.price, intlLocale)}</dd>
                </div>
              ))}
            </dl>
          </section>

          <section className="card stack-sm">
            <h2>{t('whoPaidWhat')}</h2>
            {x.customers.length === 0 ? (
              <Empty>{t('noResults')}</Empty>
            ) : (
              <ul className="list">
                {x.customers.map((c) => (
                  <li key={c.customerId}>
                    <Link to={`/customers/${c.customerId}`} className="row">
                      <span className="row-main">
                        <span className="row-title">{c.customerName}</span>
                        <span className="row-sub">
                          {formatDate(c.on, intlLocale, 'date')} · {formatQty(c.qty)} {x.packUnit}
                          {c.rule ? <span className="badge">{t(c.rule === 'fixed' ? 'fixed' : 'margin')}</span> : null}
                        </span>
                      </span>
                      <span className="row-end-value">{formatMoney(c.price, intlLocale)}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <CompetitorBlock intel={x} />
        </div>
      )}
    </QueryView>
  );
}

function CompetitorBlock({ intel }: { intel: ItemIntel }) {
  const { t, intlLocale } = useLocale();
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  const [source, setSource] = useState<ItemIntel['competitors'][number]['source']>('customer_invoice');
  const [note, setNote] = useState('');
  const competitors = useQuery({ queryKey: qk.competitors, queryFn: ({ signal }) => api.competitors(signal) });
  const save = useMutation({
    mutationFn: () => {
      const known = competitors.data?.find((c) => c.name.toLowerCase() === name.trim().toLowerCase());
      return api.recordCompetitorPrice(intel.itemId, {
        ...(known ? { competitorId: known.id } : { competitorName: name.trim() }),
        price: parseNumber(price) ?? 0,
        source,
        ...(note.trim() ? { note: note.trim() } : {}),
      });
    },
    onSuccess: () => {
      setName('');
      setPrice('');
      setNote('');
      void qc.invalidateQueries({ queryKey: qk.intel(intel.itemId) });
      void qc.invalidateQueries({ queryKey: qk.competitors });
    },
  });
  const n = parseNumber(price);
  return (
    <section className="card stack-sm">
      <h2>{t('competitors')}</h2>
      {intel.competitors.length === 0 ? (
        <p className="muted">{t('noCompetitorPrices')}</p>
      ) : (
        <ul className="list">
          {intel.competitors.map((c) => (
            <li key={c.id} className="row">
              <span className="row-main">
                <span className="row-title">{c.competitorName}</span>
                <span className="row-sub">
                  {formatDate(c.seenOn, intlLocale, 'date')} · {t(`cps_${c.source}`)}
                  {c.customerName ? ` · ${c.customerName}` : ''}
                  {c.note ? ` · ${c.note}` : ''}
                </span>
              </span>
              <span className="row-end-value">{formatMoney(c.price, intlLocale)}</span>
            </li>
          ))}
        </ul>
      )}
      <form
        className="stack-sm"
        onSubmit={(e) => {
          e.preventDefault();
          if (name.trim() && n !== null && n > 0) save.mutate();
        }}
      >
        <span className="label">{t('seenPrice')}</span>
        <div className="picker-head">
          <input type="text" list="competitor-names" autoComplete="off" placeholder={t('competitor')} aria-label={t('competitor')} value={name} onChange={(e) => setName(e.target.value)} style={{ flex: 1, minWidth: 120 }} />
          <datalist id="competitor-names">{(competitors.data ?? []).map((c) => <option key={c.id} value={c.name} />)}</datalist>
          <input className="count-input" type="text" inputMode="decimal" autoComplete="off" placeholder="$" aria-label={t('price')} value={price} onChange={(e) => setPrice(e.target.value)} />
        </div>
        <div className="picker-head">
          <select value={source} onChange={(e) => setSource(CompetitorPriceSource.parse(e.target.value))} aria-label={t('seenWhere')} style={{ flex: 1 }}>
            {CompetitorPriceSource.options.map((s) => (
              <option key={s} value={s}>
                {t(`cps_${s}`)}
              </option>
            ))}
          </select>
          <input type="text" autoComplete="off" placeholder={t('notes')} aria-label={t('notes')} value={note} onChange={(e) => setNote(e.target.value)} style={{ flex: 1, minWidth: 100 }} />
        </div>
        <button type="submit" className="btn btn-secondary btn-lg btn-block" disabled={save.isPending || !name.trim() || n === null || n <= 0}>
          {save.isPending ? t('sending') : t('recordSeenPrice')}
        </button>
        {save.error ? <ErrorBox error={save.error} /> : null}
      </form>
    </section>
  );
}
